<template>
    <v-row v-if="!loader.isLoading || silentUpdate" no-gutters style="max-width: 100%;">
        <template v-if="isEmailingEnabled">
            <v-col cols="12" class="mb-3">
                <Email
                    :account-id="companyId"
                    :client-id="project ? project.client_id : client.id"
                    :project-id="project ? project.id : null"
                    :contact-tabs="contactTabs"
                    :automations="plannedTaskAutomations"
                    :templates="[]"
                    :client-locale-code="project ? project.client_id_disp.locale_code : client.locale_code"
                    :editing-automation-data="editAutomationData"
                    :only-automations="false"
                    class="elevation-4"
                    @saveAutomation="onSave"
                />
            </v-col>
            <v-col cols="12" class="mt-3 mb-3">
                <Planned
                    :email-automations="plannedTaskAutomations"
                    @edit-automation="editAutomationId = $event"
                />
            </v-col>
        </template>
        <v-col v-else cols="12" class="mb-3">
            <EmailDisabled
                :company-id="companyId"
            />
        </v-col>
        <v-col v-if="emailLogs && emailLogs.length" cols="12" class="mt-3">
            <History
                :show-project-type="!project"
                :email-logs="emailLogs"
                :client-id="project ? project.client_id : client.id"
                @update-pin="onUpdatePin"
                @reload="reloadData"
            />
        </v-col>
    </v-row>
</template>

<script>
import {ref, computed, watch, onMounted, reactive} from "@vue/composition-api";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import Planned from "@/views/Client/Communication/Planned/Index"
import ApiClientCommunication from "@/api/client/communication"
import History from "@/views/Client/Communication/History/Index"
import Email from "@/views/Client/Communication/Email/Index"
import {$t} from "@/i18n";
import AccountModule from "@/store/modules/account";
import { APP_STORE_APP_TYPES } from "@/helpers/constants";
import EmailDisabled from "@/views/Client/Communication/EmailDisabled/Index"
import Loader from "@/utility/loader";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "Communication",
    components: {
        Planned,
        History,
        Email,
        EmailDisabled
    },
    props: {
        client: {
            type: Object,
            default: null
        },
        project: {
            type: Object,
            default: null
        }
    },
    setup(props) {
        const accountModule = AccountModule();
        const plannedTaskAutomations = ref([]);
        const emailLogs = ref([]);
        const contactTabs = ref([]);
        const editAutomationId = ref(null);
        const silentUpdate = ref(false);
        const loader = reactive(new Loader());
        const { showFeedbackNotification } = FeedbackNotificationModule();

        const editAutomationData = computed(() => plannedTaskAutomations.value.find(x => x.id === editAutomationId.value))

        const companyId = computed(() => {
            return props.project ? props.project.account_id : props.client.account_id
        })

        const loadData = async () => {
            await loader.withLoader(async () => {
                try {
                    const projectId = props.project ? props.project.id : null
                    const clientId = props.project ? props.project.client_id : props.client.id
                    const communicationData = await ApiClientCommunication.getCommunicationData(clientId, projectId)
                    if (communicationData) {
                        if (communicationData.task_automations) {
                            plannedTaskAutomations.value = communicationData.task_automations
                        }

                        if (communicationData.email_logs) {
                            emailLogs.value = communicationData.email_logs
                        }

                        if (communicationData.from_contact_tabs) {
                            contactTabs.value = communicationData.from_contact_tabs
                        }
                    }

                } catch (e) {
                    errorDialog.showError(e)
                }
            })
        }

        onMounted(() => {
            loadData()
        })

        const onSave = async (saveData) => {
            try {
                const clientId = props.project ? props.project.client_id : props.client.id
                const projectId = props.project ? props.project.id : null

                const data = await ApiClientCommunication.saveClientAutomation(clientId, projectId, saveData)
                const automation = data.automation

                const plannedTaskAutomationsCopy = plannedTaskAutomations.value.slice()

                const existingIndex = plannedTaskAutomationsCopy.findIndex(x => x.id === automation.id)
                if (existingIndex === -1) {
                    plannedTaskAutomationsCopy.unshift(automation)
                } else {
                    plannedTaskAutomationsCopy[existingIndex] = automation
                }

                plannedTaskAutomations.value = plannedTaskAutomationsCopy
                editAutomationId.value = null
                showFeedbackNotification($t('Email added to queue'))

            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const onUpdatePin = async (data) => {
            /*try {
                await ApiClientCommunication.postEmailLogPin(data.emailLogId, data.isPinned, props.client.id)
                const emailLog = emailLogs.value.find(x => x.id === data.emailLogId)
                emailLog.is_pinned = data.isPinned
            } catch (e) {
                errorDialog.showError(e)
            }*/
        }

        const reloadData = async () => {
            // this keeps the view from flickering when the data reloads
            silentUpdate.value = true
            await loadData()
            silentUpdate.value = false
        }

        const isEmailingEnabled = computed(() => accountModule.isFeatureEnabled(companyId.value, APP_STORE_APP_TYPES.EMAILING, false))

        watch (() => props.client, () => {
            loadData()
        }, { immediate: true })

        return {
            plannedTaskAutomations,
            emailLogs,
            onSave,
            contactTabs,
            editAutomationId,
            editAutomationData,
            onUpdatePin,
            isEmailingEnabled,
            loader,
            companyId,
            reloadData,
            silentUpdate
        };
    }
};
</script>
